import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OnboardingGroupsResult, OnboardingState, PairingCode } from '@cloudsign/shared';
import type { GroupTemplateKey, OnboardingStepKey } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Alert, Badge, Button, Card, CheckboxField, LinkButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { useSite } from '../../lib/site';
import { timeZoneNames } from '../sites/api';
import { ONBOARDING, spacedCode } from './api';

const KEY = ['onboarding'] as const;
const STEP_ORDER: OnboardingStepKey[] = ['branding', 'site', 'groups', 'kiosk', 'users'];
const TEMPLATES: GroupTemplateKey[] = ['visitors', 'staff', 'contractors'];

function useSetState() {
  const qc = useQueryClient();
  return (s: OnboardingState) => qc.setQueryData(KEY, s);
}

function StatusBadge({ status }: { status: 'todo' | 'done' | 'skipped' }) {
  return <Badge tone={status === 'done' ? 'good' : 'neutral'}>{t(`setup.wizard.status.${status}`)}</Badge>;
}

function MarkButtons({ step, status }: { step: OnboardingStepKey; status: 'todo' | 'done' | 'skipped' }) {
  const onSuccess = useSetState();
  const mark = useMutation({ mutationFn: (s: 'done' | 'skipped') => api.patch(`${ONBOARDING}/steps/${step}`, { body: { status: s }, schema: OnboardingState }), onSuccess });
  if (status === 'done') return null;
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <ErrorLine error={mark.error} />
      <Button variant="primary" disabled={mark.isPending} onClick={() => mark.mutate('done')}>
        {t('setup.wizard.markDone')}
      </Button>
      {status === 'todo' ? (
        <Button disabled={mark.isPending} onClick={() => mark.mutate('skipped')}>
          {t('setup.wizard.skip')}
        </Button>
      ) : null}
    </div>
  );
}

function SiteStep({ done }: { done: boolean }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [timeZone, setTimeZone] = useState('Europe/London');
  const onSuccess = useSetState();
  const create = useMutation({ mutationFn: () => api.post(`${ONBOARDING}/site`, { body: { name, address, timeZone }, schema: OnboardingState }), onSuccess });
  const errs = fieldErrors(create.error);
  if (done) return <p className="text-sm text-slate-700">{t('setup.wizard.site.done')}</p>;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <ErrorLine error={create.error} />
      </div>
      <TextField label={t('setup.wizard.site.name')} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} />
      <SelectField label={t('setup.wizard.site.timeZone')} value={timeZone} onChange={(e) => setTimeZone(e.target.value)} error={errs['timeZone']}>
        {timeZoneNames(timeZone).map((z) => (
          <option key={z} value={z}>
            {z}
          </option>
        ))}
      </SelectField>
      <TextField className="sm:col-span-2" label={t('setup.wizard.site.address')} maxLength={500} value={address} onChange={(e) => setAddress(e.target.value)} error={errs['address']} />
      <div className="sm:col-span-2">
        <Button type="submit" variant="primary" disabled={create.isPending || name.trim() === ''}>
          {t('setup.wizard.site.create')}
        </Button>
      </div>
    </form>
  );
}

function GroupsStep({ done, hasSite }: { done: boolean; hasSite: boolean }) {
  const qc = useQueryClient();
  const [chosen, setChosen] = useState<GroupTemplateKey[]>(TEMPLATES);
  const create = useMutation({
    mutationFn: () => api.post(`${ONBOARDING}/groups`, { body: { templates: chosen }, schema: OnboardingGroupsResult }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  if (!hasSite) return <p className="text-sm text-slate-700">{t('setup.wizard.groups.needSite')}</p>;
  const toggle = (k: GroupTemplateKey) => setChosen((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  return (
    <div className="space-y-3">
      <ErrorLine error={create.error} />
      {done ? <p className="text-sm text-slate-700">{t('setup.wizard.groups.done')}</p> : null}
      <fieldset className="space-y-2">
        <legend className="mb-1 text-sm font-medium text-slate-800">{t('setup.wizard.groups.legend')}</legend>
        {TEMPLATES.map((k) => (
          <CheckboxField key={k} label={t(`setup.wizard.groups.${k}`)} hint={t(`setup.wizard.groups.${k}Help`)} checked={chosen.includes(k)} onChange={() => toggle(k)} />
        ))}
      </fieldset>
      {create.data ? (
        <Alert kind="success">{t('setup.wizard.groups.result', { created: create.data.groups.filter((g) => g.created).length, existing: create.data.groups.filter((g) => !g.created).length })}</Alert>
      ) : null}
      <Button variant="primary" disabled={create.isPending || chosen.length === 0} onClick={() => create.mutate()}>
        {done ? t('setup.wizard.groups.again') : t('setup.wizard.groups.create')}
      </Button>
    </div>
  );
}

function KioskStep({ siteId, status }: { siteId: string | null; status: 'todo' | 'done' | 'skipped' }) {
  const [name, setName] = useState(t('setup.wizard.kiosk.defaultName'));
  const pair = useMutation({
    mutationFn: () => api.post('/kiosk-devices/pairing-codes', { body: { siteId, name, kind: 'kiosk' }, schema: PairingCode }),
  });
  if (!siteId) return <p className="text-sm text-slate-700">{t('setup.wizard.kiosk.needSite')}</p>;
  return (
    <div className="space-y-3">
      {status === 'done' ? <p className="text-sm text-slate-700">{t('setup.wizard.kiosk.done')}</p> : null}
      <ErrorLine error={pair.error} />
      {pair.data ? (
        <div role="status">
          <p className="text-3xl font-bold tracking-widest text-slate-900" aria-label={t('setup.wizard.kiosk.codeLabel', { code: pair.data.code.split('').join(' ') })}>
            <span aria-hidden="true">{spacedCode(pair.data.code)}</span>
          </p>
          <p className="mt-1 text-sm text-slate-600">{t('setup.wizard.kiosk.codeHelp')}</p>
        </div>
      ) : (
        <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
          <TextField label={t('setup.wizard.kiosk.name')} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          <Button variant="primary" disabled={pair.isPending || name.trim() === ''} onClick={() => pair.mutate()}>
            {t('setup.wizard.kiosk.getCode')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Getting-started wizard (onboarding.md). Reads GET /onboarding and writes through POST /onboarding/site, POST /onboarding/groups,
 * PATCH /onboarding/steps/:key and POST /onboarding/dismiss. Branding and users are done on their own pages; the wizard links
 * there and lets the admin mark the step. The kiosk step asks the existing pairing-code route for the wizard's site.
 */
export function WizardPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get(ONBOARDING, { schema: OnboardingState }) });
  const onSuccess = useSetState();
  const dismiss = useMutation({ mutationFn: () => api.post(`${ONBOARDING}/dismiss`, { schema: OnboardingState }), onSuccess });
  const { siteId: activeSite } = useSite();
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const state = q.data;
  const status = (k: OnboardingStepKey) => state.steps.find((s) => s.key === k)?.status ?? 'todo';
  const doneCount = state.steps.filter((s) => s.status !== 'todo').length;

  return (
    <>
      <PageHeader
        title={t('setup.wizard.title')}
        description={t('setup.wizard.description')}
        actions={
          state.completedAt ? null : (
            <Button disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
              {t('setup.wizard.dismiss')}
            </Button>
          )
        }
      />
      <ErrorLine error={dismiss.error} />
      {state.completedAt ? (
        <div className="mb-6">
          <Alert kind="success">{t('setup.wizard.complete')}</Alert>
        </div>
      ) : (
        <p className="mb-4 text-sm text-slate-600" role="status">
          {t('setup.wizard.progress', { done: doneCount, total: STEP_ORDER.length })}
        </p>
      )}
      <ol className="space-y-4">
        {STEP_ORDER.map((k, i) => {
          const s = status(k);
          return (
            <li key={k}>
              <Card
                title={
                  <span className="flex flex-wrap items-center gap-3">
                    <span>{t('setup.wizard.stepTitle', { n: i + 1, title: t(`setup.wizard.step.${k}`) })}</span>
                    <StatusBadge status={s} />
                  </span>
                }
                description={t(`setup.wizard.step.${k}Help`)}
              >
                {k === 'branding' ? (
                  <LinkButton to="/admin/settings">{t('setup.wizard.branding.open')}</LinkButton>
                ) : null}
                {k === 'users' ? <LinkButton to="/admin/users">{t('setup.wizard.users.open')}</LinkButton> : null}
                {k === 'site' ? <SiteStep done={s === 'done'} /> : null}
                {k === 'groups' ? <GroupsStep done={s === 'done'} hasSite={state.siteId !== null || status('site') === 'done'} /> : null}
                {k === 'kiosk' ? <KioskStep siteId={state.siteId ?? activeSite} status={s} /> : null}
                {k === 'branding' || k === 'users' || k === 'kiosk' ? <MarkButtons step={k} status={s} /> : null}
              </Card>
            </li>
          );
        })}
      </ol>
      <p className="mt-6 text-sm text-slate-600">
        <Link className="underline" to="/getting-started">
          {t('setup.wizard.backToStart')}
        </Link>
      </p>
    </>
  );
}
